/*
 * Collaboration
 *
 * A Collaboration is workflow related to a Feature
 */
import { mergeRight, pick, pluck, renameKeys, tagged } from '@range.io/functional'
import CanvasSelectors from 'range-editor/src/redux/persisted-state/canvas-slice/canvas-selectors.js'
import CommentSelectors from 'range-editor/src/redux/persisted-state/comment-slice/comment-selectors.js'
import GeometrySelectors from 'range-editor/src/redux/persisted-state/geometry-slice/geometry-selectors.js'
import ParticipantSelectors from 'range-editor/src/redux/persisted-state/participant-slice/participant-selectors.js'
import StatusNameSelectors from 'range-editor/src/redux/persisted-state/status-name-slice/status-name-selectors.js'
import TagNameSelectors from 'range-editor/src/redux/persisted-state/tag-name-slice/tag-name-selectors.js'
import UpdateSelectors from 'range-editor/src/redux/persisted-state/update-slice/update-selectors.js'
import UploadSelectors from 'range-editor/src/redux/persisted-state/upload-slice/upload-selectors.js'
import { dateToTimestamp, millisOrTimestampToDate } from '../helper/timestamp.js'
import StringTypes from '../string-types.js'

// ---------------------------------------------------------------------------------------------------------------------
// Definitions
// ---------------------------------------------------------------------------------------------------------------------

// prettier-ignore
const Collaboration = tagged('Collaboration', {
    id             : StringTypes.Id,
    identifier     : 'String?', // human-readable number rather than UUID
    createdAt      : 'Object', // Date
    createdById    : StringTypes.OptionalId, // -> Participant id
    name           : 'String',
    followerIds    : StringTypes.Ids, // -> [User id]
    
    // for Issue Collaborations only
    statusNameId   : StringTypes.OptionalId, // -> StatusName id
    description    : 'String?',
    tagNameIds     : StringTypes.Ids, // -> [TagName id]
    assigneeId     : StringTypes.OptionalId,
    dueDate        : 'Object?', // Date
    storageVersion : 'Object?',
    
    featureId      : StringTypes.Id,
    geometryId     : StringTypes.Id,
    canvasId       : StringTypes.Id,
    projectId      : StringTypes.Id,
    organizationId : StringTypes.Id,
    
    categoryId     : StringTypes.Id,
})

// TODO: remove renames
Collaboration.prototype.renameFieldButLoudly('followers', 'followerIds')
Collaboration.prototype.renameFieldButLoudly('parentId', 'featureId')

/*
 * Is this Collaboration a Task, rather than a Photo Marker?
 * TODO: convert Collaboration to a taggedSum with Task and PhotoMarker constructors
 */
Collaboration.isTask = collaboration => !!collaboration.statusNameId

// ---------------------------------------------------------------------------------------------------------------------
// Serialization
// ---------------------------------------------------------------------------------------------------------------------

/*
 * Firebase doesn't like arrays and will not store an empty array
 * @sig fromFirebase = {k:v} -> Collaboration
 */
Collaboration.fromFirebase = o =>
    Collaboration.from({
        id: o.id,
        identifier: o.identifier,
        createdAt: millisOrTimestampToDate(o.createdAt),
        createdById: o.createdBy,
        name: o.name || '',
        followerIds: o.followers || [],

        // issue
        statusNameId: o.statusName,
        description: o.description,
        tagNameIds: o.tags || [],
        assigneeId: o.assignee,
        dueDate: millisOrTimestampToDate(o.dueDateTimestamp),

        storageVersion: o.storageVersion,

        featureId: o.featureId,
        geometryId: o.geometryId,
        canvasId: o.canvasId,
        projectId: o.projectId,
        organizationId: o.organizationId,

        categoryId: o.categoryId,
    })

Collaboration.toFirebase = o =>
    pick(
        [
            'id',
            'createdAt',
            'createdBy',
            'feature',
            'name',
            'statusNameId',
            'description',
            'tagNameIds',
            'assigneeId',
            'dueDateTimestamp',
            'storageVersion',
            'followerIds',
            'categoryId',
        ],
        renameKeys(
            {
                createdById: 'createdBy',
                createdAt: ['createdAt', () => dateToTimestamp(o.createdAt)],
                dueDate: ['dueDateTimestamp', () => dateToTimestamp(o.dueDate)],
                followerIds: 'followers',
                statusNameId: 'statusName',
                tagNameIds: 'tags',
                assigneeId: 'assignee',
            },
            o
        )
    )

// ---------------------------------------------------------------------------------------------------------------------
// Syntactic sugar for creating a specific kind of Collaboration object
// ---------------------------------------------------------------------------------------------------------------------

/*
 * Create a new Collaboration merging an old one with values from changes
 * @sig update :: (Collaboration, {k:v}) -> Collaboration
 */
Collaboration.update = (collaboration, changes) => Collaboration.from(mergeRight(collaboration, changes))

// hack: values will be replaced by GeometrySlice before they're used
Collaboration.initialCollaborationStatus = 'will be a uuid'
Collaboration.completedCollaborationStatus = 'will be a uuid'

/*
 * Extend our prototype with more functions -- but only if we're using Redux. We don't really want this file to know
 * about Redux per se, but it's nice to have these additional functions defined here if we ARE using Redux, so there's
 * only one place to look for them.
 *
 * @see store.js for calls to extendForRedux
 */
// prettier-ignore
Collaboration.extendForRedux = (getState, defineGetter, defineGetterButComplain) => {
    defineGetter(Collaboration, 'assignee',        _this => ParticipantSelectors.selectedProjectParticipantWithId(getState(), _this.assigneeId))
    defineGetter(Collaboration, 'canvas',          _this => CanvasSelectors.canvasWithId(getState(), _this.canvasId))
    defineGetter(Collaboration, 'comments',        _this => CommentSelectors.commentsForCollaboration(getState(),_this))
    defineGetter(Collaboration, 'createdBy',       _this => ParticipantSelectors.selectedProjectParticipantWithId(getState(), _this.createdById))
    defineGetter(Collaboration, 'geometry',        _this => GeometrySelectors.geometryForCollaboration(getState(), _this))
    defineGetter(Collaboration, 'updates',         _this => UpdateSelectors.updatesForCollaborationId(getState(), _this.id))
    defineGetter(Collaboration, 'statusName',      _this => StatusNameSelectors.statusNameForCollaboration(getState(), _this))
    defineGetter(Collaboration, 'tagNames',        _this => TagNameSelectors.tagNamesForCollaboration(getState(), _this))
    defineGetter(Collaboration, 'uploads',         _this => UploadSelectors.uploadsForCollaborationId(getState(), _this.id))
    defineGetter(Collaboration, 'isArchived',      _this => _this.geometry.isArchived)
    defineGetter(Collaboration, 'isComplete',      _this => !!_this.statusName?.isCompleted)
    defineGetter(Collaboration, 'isTask',          _this => !!_this.statusNameId)
    defineGetter(Collaboration, 'hasNotesPending', _this => !!_this.comments.some(c => c.isPending))
    
    defineGetter(Collaboration, 'lastUpdateDate',  _this => {
        const dates = [...pluck('createdAt', _this.updates.concat(_this.comments).concat(_this.uploads)), _this.createdAt,]
        return new Date(Math.max(...dates))
    })
    
    // rename "loudly"
    defineGetterButComplain(Collaboration, 'tags', 'tagNames')
    defineGetterButComplain(Collaboration, 'status', 'statusName')
}

export default Collaboration
